/* eslint-disable jsx-a11y/media-has-caption */
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography, IconButton } from '@mui/material';
import { PauseRounded, PlayArrowRounded } from '@mui/icons-material';
import Dot from '@src/new-components/Dot/Index';
import { COLOR } from '@src/styles/color';
import {
  StyledAudioPlay,
  StyledAudioPlayerBox,
  StyledNewAudioSlider,
} from './index.style';

const TYPE_AUDIO = {
  original: 'original',
  clone: 'clone',
};

const AudioPlayerBox = ({
  type,
  title,
  color,
  isPlaying,
  sliderValue,
  onTogglePlay,
  onSliderChange,
  disabled,
}) => {
  const { t } = useTranslation();

  return (
    <StyledAudioPlayerBox type={type}>
      <Box display="flex" alignItems="center" gap={1} mb="4px">
        <Dot color={color} />
        <Typography variant="subtitle1" align="center">
          {t(title)}
        </Typography>
      </Box>
      <StyledAudioPlay
        className="audio-sample-wrapper"
        bgcolor={type === TYPE_AUDIO.original ? COLOR.darkBlue : COLOR.primary}
        color={type === TYPE_AUDIO.original ? COLOR.white : COLOR.darkBlue}
      >
        <IconButton
          className={`button-play ${disabled ? 'disabled' : ''}`}
          onClick={() => onTogglePlay(type)}
          disabled={disabled}
        >
          {isPlaying ? <PauseRounded /> : <PlayArrowRounded />}
        </IconButton>
        <StyledNewAudioSlider
          value={sliderValue}
          step={1}
          min={0}
          max={100}
          onChange={(_, newValue) => onSliderChange(type, newValue)}
          style={{
            color:
              type === TYPE_AUDIO.original ? COLOR.darkBlue : COLOR.primary,
          }}
        />
      </StyledAudioPlay>
    </StyledAudioPlayerBox>
  );
};

const AudioPlay = ({ originalAudioLink, cloneAudioLink }) => {
  const originalAudioRef = useRef(null);
  const cloneAudioRef = useRef(null);

  const [playAudio, setPlayAudio] = useState({ original: false, clone: false });
  const [sliderValue, setSliderValue] = useState({ original: 0, clone: 0 });

  const handleTogglePlay = (type) => {
    const currentRef =
      type === TYPE_AUDIO.original
        ? originalAudioRef.current
        : cloneAudioRef.current;
    const otherRef =
      type === TYPE_AUDIO.original
        ? cloneAudioRef.current
        : originalAudioRef.current;

    const isCurrentlyPlaying = playAudio[type];

    if (isCurrentlyPlaying) {
      currentRef.pause();
    } else {
      currentRef.play();
      otherRef.pause();
    }

    setPlayAudio({
      original: type === TYPE_AUDIO.original ? !isCurrentlyPlaying : false,
      clone: type === TYPE_AUDIO.clone ? !isCurrentlyPlaying : false,
    });
  };

  const handleSliderChange = (type, newValue) => {
    const audioRef =
      type === TYPE_AUDIO.original
        ? originalAudioRef.current
        : cloneAudioRef.current;
    const newTime = (newValue / 100) * audioRef.duration;
    audioRef.currentTime = newTime;
    setSliderValue((prev) => ({ ...prev, [type]: newValue }));
  };

  const handleTimeUpdate = (type) => {
    const audioRef =
      type === TYPE_AUDIO.original
        ? originalAudioRef.current
        : cloneAudioRef.current;
    const percentage = (audioRef.currentTime / audioRef.duration) * 100;
    setSliderValue((prev) => ({ ...prev, [type]: percentage }));
  };

  const handleEnded = (type) => {
    setPlayAudio((prev) => ({ ...prev, [type]: false }));
    setSliderValue((prev) => ({ ...prev, [type]: 0 }));
  };

  return (
    <Grid container spacing={2} mb={3} mt={0}>
      <Grid item xs={12} sm={6}>
        <AudioPlayerBox
          type={TYPE_AUDIO.original}
          title="originalVoice"
          color="#1E164E"
          audioRef={originalAudioRef}
          isPlaying={playAudio.original}
          sliderValue={sliderValue.original}
          onTogglePlay={handleTogglePlay}
          onSliderChange={handleSliderChange}
          disabled={!originalAudioLink}
        />
        <audio
          ref={originalAudioRef}
          src={originalAudioLink}
          style={{ display: 'none' }}
          onEnded={() => handleEnded(TYPE_AUDIO.original)}
          onTimeUpdate={() => handleTimeUpdate(TYPE_AUDIO.original)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AudioPlayerBox
          type={TYPE_AUDIO.clone}
          title="instantVoiceCloneLabel"
          color="#34C759"
          audioRef={cloneAudioRef}
          isPlaying={playAudio.clone}
          sliderValue={sliderValue.clone}
          onTogglePlay={handleTogglePlay}
          onSliderChange={handleSliderChange}
          disabled={!cloneAudioLink}
        />
        <audio
          ref={cloneAudioRef}
          src={cloneAudioLink}
          style={{ display: 'none' }}
          onEnded={() => handleEnded(TYPE_AUDIO.clone)}
          onTimeUpdate={() => handleTimeUpdate(TYPE_AUDIO.clone)}
        />
      </Grid>
    </Grid>
  );
};

export default AudioPlay;
